<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import JournalCardStack from '@/vue/molecules/today/journal-card-stack.vue';
import JournalEntryForm from '@/vue/molecules/today/journal-entry-form.vue';
import JournalMoodCard from '@/vue/molecules/today/journal-mood-card.vue';
import type {JournalEntryDraft, JournalFeelingOption, StackEntry} from '@/ts/types/component/today-page';
import type {MoodDTO} from '@/ts/types/dto/journals-dto';
import {localEntryId} from '@/ts/composables/stateful/use-journal-tile';
import {useSnackbarStore} from '@/ts/store/snackbar-store';
import {pagesPath} from '@/ts/router/pages-path';

/**
 * Journal tile for the bento Today page.
 *
 * Sizing: the tile fills whatever cell the bento gives it. Its internal layout
 * is driven by container queries, so it lays out from its own width rather than
 * the viewport's and stays correct at any width it ends up with.
 *
 * Layout by tile width: two columns (intro/form left, card stack right) when
 * wide, and below that the stack is dropped entirely and the tile flips to
 * reveal the saved entry. Two states, not three — the stack belongs beside the
 * intro or nowhere, and there used to be a band that put it underneath.
 *
 * Usage — the tile renders and validates, the parent owns the data:
 *
 * ```vue
 * <journal-tile
 *   ref="tileRef"
 *   :entries="entries"
 *   :loading="loading"
 *   :saving="saving"
 *   :feelings-by-mood="feelingsByMood"
 *   @save="onSave"
 * />
 * ```
 *
 * `save` does **not** mean saved. Persist the draft yourself, then call
 * `tileRef.confirmSaved({mood, text})` — only then does the tile clear the form
 * and show its confirmation. A failed request that never confirms leaves the
 * user's draft intact, which is the point.
 *
 * Pass `loading` while the entries are in flight, or a returning user sees a
 * flash of the empty state before their own entries arrive.
 */
const props = withDefaults(defineProps<{
  entries?: StackEntry[];
  feelingsByMood?: Record<string, JournalFeelingOption[]>;
  defaultFeelings?: JournalFeelingOption[];
  saving?: boolean;
  /**
   * The entries are still being fetched. Without this the tile cannot tell an
   * empty journal from an unanswered request, and a returning user would see a
   * flash of the empty state — intro plus example cards — before their own
   * entries land.
   */
  loading?: boolean;
}>(), {
  entries: () => [],
  feelingsByMood: () => ({}),
  defaultFeelings: () => [],
  saving: false,
  loading: false,
});

const emit = defineEmits<{
  save: [payload: JournalEntryDraft];
}>();

const {t} = useI18n();

/**
 * Example cards behind the empty state, so a first-time user sees what an entry
 * will look like. Fixed illustrative dates, formatted through the active locale.
 */
const EXAMPLE_ENTRIES = computed<StackEntry[]>(() => [
  {id: 'example-1', mood: 'joyful', text: t('journal_tile_example_1'), date: '2024-04-25T18:00:00'},
  {id: 'example-2', mood: 'calm', text: t('journal_tile_example_2'), date: '2024-04-24T07:00:00'},
  {id: 'example-3', mood: 'neutral', text: t('journal_tile_example_3'), date: '2024-04-23T15:00:00'},
]);

/**
 * Resting state. With no entries the tile shows the intro (its empty state);
 * once the user has journalled once there is no intro to return to, so the
 * mood picker is what the tile rests on — matching the design.
 */
const showForm = ref(false);
const leaving = ref(false);
/** Mask animating out; the intro then animates back in. */
const closing = ref(false);
const returning = ref(false);
/** Bumped to remount the mask with a clean draft. */
const remountKey = ref(0);
const flipped = ref(false);
const lastSaved = ref<StackEntry | null>(null);
const rootRef = ref<HTMLElement | null>(null);
/** True while the tile is too narrow for the card stack — the flip layout. */
const isNarrow = ref(false);

let resizeObserver: ResizeObserver | undefined;
let entryTimer: number | undefined;
let closeTimer: number | undefined;
let returnTimer: number | undefined;

/** Opening: intro out (220ms) then mask in. Closing plays the exact inverse. */
const OUT_MS = 220;
const IN_MS = 340;


// The stack only exists in the two-column layout, so anything narrower than that is the flip
// tile. It used to be 419, which left a band where the stack came back stacked UNDER the intro
// instead of beside it (IPD-3110).
//
// Read as "below the stack's width" rather than "at most one less": a container's width can be
// fractional, and a tile of 559.5px is narrower than the two-column layout without being 559.
const STACK_MIN_WIDTH = 560;

function measure(): void {
  const el = rootRef.value;
  if (!el) {
    return;
  }
  isNarrow.value = el.clientWidth < STACK_MIN_WIDTH;
}

onMounted(() => {
  if (typeof ResizeObserver !== 'undefined' && rootRef.value) {
    // observe() delivers the initial size, so no explicit first measure —
    // that would force a synchronous layout before the first paint.
    resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(rootRef.value);
  } else {
    measure();
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  window.clearTimeout(entryTimer);
  window.clearTimeout(closeTimer);
  window.clearTimeout(returnTimer);
});

// The flip only exists on narrow tiles; widening must not strand the tile
// showing a stale back face with no way to turn it over.
watch(isNarrow, (narrow) => {
  if (!narrow) {
    flipped.value = false;
  }
});

/**
 * True once the user has journalled at least once. `lastSaved` counts: right
 * after the first save the parent has not refetched yet, and without it the
 * tile would drop back to its empty state for a frame.
 */
const hasJournalled = computed(() => props.entries.length > 0 || lastSaved.value !== null);
/**
 * Until the first real entry exists the examples stand in for it, showing a
 * first-time user what an entry looks like. They are never mixed with real
 * ones — once the user has journalled, only their own entries show.
 */
const stackEntries = computed(() => (hasJournalled.value ? props.entries : EXAMPLE_ENTRIES.value));

/**
 * Whether the entries have come back at least once. Sticky on purpose: only the
 * *first* load is unknown. Treating every later refetch as unresolved would
 * unmount the card stack on each save, and an unmount kills the animation that
 * slides the new entry to the front.
 */
const isResolved = ref(!props.loading);
watch(() => props.loading, (loading) => {
  if (!loading) {
    isResolved.value = true;
  }
});

const hasStack = computed(() => isResolved.value && stackEntries.value.length > 0);

// The resting state follows from having journalled: whether that comes from the
// initial fetch or from a first save, the empty state is over for good.
watch(hasJournalled, (has) => {
  if (has) {
    showForm.value = true;
  }
}, {immediate: true});

function beginEntry(): void {
  // The intro animates out while still mounted, then the form animates in.
  leaving.value = true;
  entryTimer = window.setTimeout(() => {
    leaving.value = false;
    showForm.value = true;
  }, OUT_MS);
}

/**
 * Back to the resting state, discarding the draft.
 *
 * With no entries this runs the opening sequence backwards: the mask animates
 * out with the easing the intro used to leave, then the intro animates back in
 * with the easing the mask used to arrive. Once the user has journalled there
 * is no intro to return to — the picker itself is the resting state, so the
 * mask is remounted clean instead.
 */
function closeMask(): void {
  if (hasJournalled.value) {
    remountKey.value += 1;
    return;
  }
  closing.value = true;
  closeTimer = window.setTimeout(() => {
    closing.value = false;
    showForm.value = false;
    returning.value = true;
    returnTimer = window.setTimeout(() => {
      returning.value = false;
    }, IN_MS);
  }, OUT_MS);
}

const snackbarStore = useSnackbarStore();

function onSave(payload: JournalEntryDraft): void {
  // Nothing is confirmed here — the parent owns the request. It calls
  // confirmSaved() once the entry is stored, so a failed save leaves the mask
  // intact with the user's draft rather than pretending it worked.
  emit('save', payload);
}

/**
 * Called by the parent once the entry is persisted. The draft is cleared so the
 * user is not left staring at what they just saved; the tile then rests on a
 * fresh picker, which from here on is its resting state.
 */
function confirmSaved(entry: {mood: MoodDTO; text: string}): void {
  // Raised here and nowhere earlier: this is the one point at which the entry is known to be in
  // the Journal, which is what the snackbar tells the reader (IPD-3215). A second entry saved while
  // the first confirmation is still up replaces it, with a countdown of its own.
  //
  // With a Dismiss, like every snackbar with an action: on a phone it also rests over the foot of the
  // flipped face, where "New entry" is, so the reader can take it down instead of waiting it out.
  snackbarStore.show({
    message: t('today_journal_snackbar_saved'),
    action: {label: t('today_journal_snackbar_action'), to: pagesPath.main.journal},
    dismissible: true,
  });
  lastSaved.value = {
    id: localEntryId(),
    mood: entry.mood,
    text: entry.text || null,
    date: new Date().toISOString(),
  };
  // Closing unmounts the mask, so the next one opens with a clean draft.
  closeMask();
  if (isNarrow.value) {
    flipped.value = true;
  }
}

/** Abandon the entry and go back to the resting state. */
function cancelEntry(): void {
  closeMask();
}

function flipBack(): void {
  flipped.value = false;
}

defineExpose({beginEntry, confirmSaved});
</script>

<template>
  <section
    ref="rootRef"
    class="journal-tile"
    :class="{flipped}"
  >
    <div class="jt-body">
      <div
        class="jt-left"
        :class="{'is-leaving': leaving, 'is-closing': closing, 'is-returning': returning}"
      >
        <journal-entry-form
          v-if="showForm"
          :key="remountKey"
          :has-intro="!hasJournalled"
          :feelings-by-mood="feelingsByMood"
          :default-feelings="defaultFeelings"
          :saving="saving"
          @save="onSave"
          @cancel="cancelEntry"
        />
        <template v-else-if="isResolved">
          <div>
            <h2 class="jt-h">
              {{ $t('journal_tile_intro_title') }}
            </h2>
            <p class="jt-p">
              {{ $t('journal_tile_intro_body') }}
            </p>
          </div>
          <div class="jt-actions">
            <button type="button" class="btn-pill" @click="beginEntry">
              {{ $t('journal_tile_cta') }}
              <svg
                class="btn-ico"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                aria-hidden="true"
              >
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          </div>
        </template>
      </div>

      <!-- Narrow tiles hide the stack in CSS anyway; not mounting it there
           avoids ~40 DOM nodes, a ResizeObserver and a matchMedia listener on
           the most memory-constrained target. -->
      <journal-card-stack v-if="hasStack && !isNarrow" class="jt-stack" :entries="stackEntries"/>
    </div>

    <!-- Narrow widths have no stack, so the tile flips to reveal the saved entry. -->
    <div v-if="lastSaved" class="jt-back" :inert="!flipped">
      <journal-mood-card
        :mood="lastSaved.mood"
        :text="lastSaved.text"
        :date="lastSaved.date"
      />
      <button type="button" class="jt-new-entry" @click="flipBack">
        {{ $t('journal_tile_new_entry') }}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      </button>
    </div>
  </section>
</template>

<style scoped lang="scss">
.journal-tile {
  // The panel's own sage, which belongs to this tile and is not on the neutral
  // scale, so it is the one value dark mode still has to swap. The ink follows
  // the scheme, which is what makes it readable on either sage.
  --journal-tile-surface: #a9c5c3;
  --journal-tile-ink: var(--Neutral-UI-neutral-ui-100);
  --journal-tile-ink-2: var(--Neutral-UI-neutral-ui-70);
  --journal-tile-on-ink: var(--Neutral-UI-neutral-ui-0);

  // The queries below size from this element, but a container query cannot
  // style its own container — hence the inner .jt-body that carries the grid.
  container-type: inline-size;
  container-name: journal-tile;
  position: relative;
  border-radius: var(--today-radius-section);
  overflow: hidden;
  background: var(--journal-tile-surface);
  color: var(--journal-tile-ink);
}

.jt-body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  align-items: center;
  padding: 28px 18px;
  // Fills whatever the bento cell gives rather than fixing a height: the grid
  // owns the vertical rhythm and equalises a row's tiles. Filling still stops
  // the tile breathing as the intro swaps for the entry mask, which a fixed
  // height was there to prevent.
  height: 100%;
}

.jt-left {
  position: relative;
  z-index: 1;
  // The intro and the mask replace each other inside this box. Without this,
  // the browser's scroll anchoring treats that swap as content shifting and
  // scrolls the surrounding page to compensate, which reads as the tile
  // jumping. The tile's own size never changes, so there is nothing to anchor.
  overflow-anchor: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 24px;
  min-width: 0;
  min-height: 0;
  height: 100%;
}

.jt-left :deep(.entry-form) {
  // Grows to the column rather than to its own content. On a wide tile the
  // column is content-sized with a floor (so large type can push it), and
  // against that `height: 100%` resolves to auto — which let the short steps,
  // the note above all, shrink the card to a strip while the mood step's tall
  // grid disguised it. The design draws one box the steps swap inside.
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
}

.jt-h {
  // Matches the design exactly: Lateef 30/27, weight 200, -0.75px tracking.
  font-family: var(--Font-Secondary-Font);
  font-weight: 200;
  font-size: 30px;
  line-height: 27px;
  letter-spacing: -0.75px;
  margin: 8px 0 0;
  max-width: 320px;
}

.jt-p {
  font-family: var(--Font-Primary-Font);
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: var(--journal-tile-ink-2);
  max-width: 400px;
  margin: 6px 0 0;
}

.jt-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  align-self: flex-start;
}

.btn-pill {
  height: 48px;
  padding: 0 24px;
  border-radius: 100px;
  background: var(--journal-tile-ink);
  color: var(--journal-tile-on-ink);
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: var(--Font-Primary-Font);
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: transform 160ms ease;

  &:hover {
    transform: translateY(-1px);
  }
}

.jt-stack {
  display: none;
  min-height: 0;
}

.jt-back {
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: absolute;
  inset: 0;
  z-index: 30;
  padding: 28px 18px;
  background: var(--journal-tile-surface);
  border-radius: var(--today-radius-section);
  transform: rotateY(-180deg);
  pointer-events: none;
}

// Narrow tile only: the tile flips to reveal the entry just saved and flips
// back via "New entry". Front and back rotate together so there is no mid-flip
// flicker — only the natural edge-on instant at 90°. Everything 3D lives in
// here so wider tiles never pay for a flip they cannot perform.
@container journal-tile (max-width: 559.98px) {
  .journal-tile {
    perspective: 1400px;
  }

  .jt-body,
  .jt-back {
    backface-visibility: hidden;
    transition: transform 560ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  .jt-body {
    transform: rotateY(0deg);
  }

  .journal-tile.flipped {
    .jt-body,
    .jt-back {
      will-change: transform;
    }

    .jt-body {
      transform: rotateY(180deg);
    }

    .jt-back {
      transform: rotateY(0deg);
      pointer-events: auto;
    }
  }
}

.jt-new-entry {
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
  color: var(--journal-tile-ink);
}

/* Intro out / form in, mirroring the design's two-part transition. */
.jt-left.is-leaving > * {
  animation: jt-intro-out 220ms cubic-bezier(0.4, 0, 1, 1) both;

  &:nth-child(2) {
    animation-delay: 45ms;
  }
}

.jt-left :deep(.entry-form) {
  animation: jt-entry-in 340ms cubic-bezier(0.22, 1, 0.36, 1) 40ms both;
}

// Closing: the mask leaves along the path it arrived by, reversed.
.jt-left.is-closing :deep(.entry-form) {
  animation: jt-entry-out 220ms cubic-bezier(0.4, 0, 1, 1) both;
}

// ...and the intro returns along the path it left by, reversed.
.jt-left.is-returning > * {
  animation: jt-intro-in 340ms cubic-bezier(0.22, 1, 0.36, 1) both;

  &:nth-child(2) {
    animation-delay: 45ms;
  }
}

@keyframes jt-intro-out {
  from {
    opacity: 1;
    transform: translateY(0);
  }

  to {
    opacity: 0;
    transform: translateY(-10px);
  }
}

@keyframes jt-entry-in {
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.985);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes jt-entry-out {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  to {
    opacity: 0;
    transform: translateY(14px) scale(0.985);
  }
}

@keyframes jt-intro-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Wide tile: the design's 50/50 split — intro left, fanned stack right. The
   design's desktop tile is ~668px wide with 48px padding, i.e. ~572px of
   content, so the split starts just under that. */
@container journal-tile (min-width: 560px) {
  // Turned on here and nowhere else: this is the only layout with a column to put it in.
  .jt-stack {
    display: flex;
  }

  // Wide enough for the stack, so the flip face is never used.
  .jt-back {
    display: none;
  }

  .jt-body {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    // 60px column gap is what puts the two columns at 256px inside a 668px
    // tile, matching the design.
    gap: 60px;
    min-height: 600px;
    // The design swaps 84px padding for 42px when the mask opens; both centre
    // their content, so it looks the same either way. We hold it at the smaller
    // value permanently: that keeps the grid row a constant height, which is
    // what stops the card stack from re-centring when the mask mounts.
    padding: 42px 48px;
    align-items: center;
  }

  // Both states occupy exactly the mask's height, so the row never resizes and
  // the stack beside it cannot shift. A floor and not a height, so larger type
  // grows the column instead of being clipped; `height: auto` is needed to stop
  // the base `height: 100%` filling the whole row and undoing the centring.
  .jt-left {
    height: auto;
    min-height: 500px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .jt-left.is-leaving > *,
  .jt-left.is-returning > *,
  .jt-left.is-closing :deep(.entry-form),
  .jt-left :deep(.entry-form) {
    animation: none;
  }

  .btn-pill {
    transition: none;
  }
}

/* Dark mode: the panel becomes deep sage with light ink, exactly as the design
   does it. The mood cards keep their pastel identity — like sticky notes on a
   dark panel — so they restore dark ink themselves (see journal-mood-card). */
@include dark-mode {
  .journal-tile {
    // Only the panel's own sage: the three ink values follow the scheme now.
    // They shift slightly from what this block used to restate by hand — the
    // scheme's own neutrals rather than the prototype's approximations of them.
    --journal-tile-surface: #2e4147;
  }
}
</style>
